import { z } from 'zod'
import { temporaryGrantSchema } from './grants'
import { rbacSlugSchema } from './rbac'

/**
 * Resource-scoped access — who holds which role on one account, and what the caller may do there.
 *
 * The two halves have deliberately different audiences, which is why they end up behind different
 * gates. Assigning and revoking is administration: it changes someone else's access. Asking
 * "what may *I* do here" is self-service — every user needs it to render a screen correctly, and
 * gating it on an admin permission would mean only admins could see correctly-gated screens.
 *
 * Resources are named the way FGA names them: by our own external id (`accounts.id`) plus the
 * type slug, so neither side keeps a lookup table of WorkOS's internal ids.
 */

export const resourceRefSchema = z.object({
  resourceType: rbacSlugSchema,
  /** Our external id — `accounts.id` for an account. */
  resourceId: z.string().min(1).max(256),
})

export const assignResourceRoleSchema = resourceRefSchema.extend({
  /** WorkOS organization membership (`om_…`) receiving the role. */
  membershipId: z.string().min(1),
  roleSlug: rbacSlugSchema,
})

export const resourceAssignmentSchema = z.object({
  /** WorkOS role-assignment id — the handle for revocation. */
  id: z.string(),
  membershipId: z.string(),
  roleSlug: z.string(),
  /**
   * 'direct' when assigned to this membership, 'group' when it comes from a group. Group-derived
   * assignments are shown but not revocable here — you'd remove the member from the group.
   */
  source: z.enum(['direct', 'group']),
  email: z.string().nullable(),
  name: z.string().nullable(),
})

/**
 * One capability a person holds here, and whether this screen can take it away.
 *
 * `source` is the difference between a checkbox and a statement of fact: 'direct' is an assignment
 * on this very resource and is revocable here; 'inherited' comes from the parent account and would
 * have to be revoked there; 'group' comes from a WorkOS group and is revoked by changing group
 * membership. Rendering all three as identical checkboxes would produce controls that silently
 * fail — the cascade is the whole point of the hierarchy, so it has to be visible, not editable.
 */
export const heldCapabilitySchema = z.object({
  capabilityId: z.string(),
  source: z.enum(['direct', 'inherited', 'group']),
  /** The assignments carrying it — what revocation removes. Empty for inherited. */
  assignmentIds: z.array(z.string()),
})

/**
 * Access grouped by PERSON rather than by assignment.
 *
 * The raw list is one row per role assignment, which is WorkOS's shape and not a useful answer to
 * "who can touch this account" — one person holding two capabilities appeared twice, under two
 * role names they were never supposed to see.
 */
export const resourceAccessPersonSchema = z.object({
  membershipId: z.string(),
  email: z.string().nullable(),
  name: z.string().nullable(),
  capabilities: z.array(heldCapabilitySchema),
  /**
   * Roles held here that the capability catalogue doesn't name — assigned in the WorkOS dashboard,
   * or left over from a catalogue change. Surfaced by slug rather than hidden: access this screen
   * can't explain is exactly the access an admin most needs to know about. They carry their
   * assignment ids because revoking one has no capability to name — it goes by assignment.
   */
  unnamedRoles: z.array(z.object({ roleSlug: z.string(), assignmentIds: z.array(z.string()) })),
})

export const listResourceAssignmentsResponseSchema = z.object({
  assignments: z.array(resourceAssignmentSchema),
  people: z.array(resourceAccessPersonSchema),
})

/**
 * Granting by email and capability — the only granting verb the UI has.
 *
 * Deliberately not `roleSlug`: WorkOS carries permissions on roles and has no direct user→
 * permission assignment, but that is a fact about their model, not a concept an admin granting
 * someone access to an account should have to hold. The server picks the role that carries this
 * capability on this resource type.
 */
export const grantCapabilitySchema = resourceRefSchema.extend({
  email: z.email(),
  /**
   * Every capability to grant, in one request — not one request each. An invitation is sent to
   * someone who isn't here yet, and looping on the client would send them one email per
   * capability. The baseline capability is expected to be in this list; the server does not add it.
   */
  capabilityIds: z.array(z.string().min(1).max(64)).min(1).max(16),
  /**
   * When present, the access revokes itself at this moment instead of standing — the same
   * time-boxed grant the access page issues org-wide (CLI-602), scoped to this resource.
   *
   * Deliberately a field on granting rather than a separate verb: "give Ana edit on this account,
   * until Friday" is one decision, and splitting it into two forms is how somebody ends up
   * granting permanent access from the wrong one.
   */
  expiresAt: z.iso.datetime().optional(),
  /** Why — recorded on the grant row, which is the audit trail and is never deleted. */
  reason: z.string().max(500).optional(),
})

export const grantCapabilityResponseSchema = z.object({
  /** 'assigned' when they were already a member; 'invited' when the grant waits on their sign-in. */
  outcome: z.enum(['assigned', 'invited']),
  email: z.string(),
  capabilityIds: z.array(z.string()),
})

export const revokeCapabilitySchema = resourceRefSchema.extend({
  membershipId: z.string().min(1),
  capabilityId: z.string().min(1).max(64),
})

/**
 * Granting access to somebody by email, whether or not they're already here.
 *
 * An FGA assignment needs an organization membership, and an invited person has none until they
 * accept — so this is one request that resolves to one of two outcomes: assigned now, or promised
 * and applied at their first sign-in.
 */
export const inviteToResourceSchema = resourceRefSchema.extend({
  email: z.email(),
  roleSlug: rbacSlugSchema,
})

export const inviteToResourceResponseSchema = z.object({
  /** 'assigned' when they were already a member; 'invited' when the grant is waiting on them. */
  outcome: z.enum(['assigned', 'invited']),
  email: z.string(),
  roleSlug: z.string(),
})

/** Access promised to someone who hasn't signed in yet — shown alongside real assignments. */
export const pendingResourceGrantSchema = z.object({
  id: z.string(),
  email: z.string(),
  roleSlug: z.string(),
  createdAt: z.string(),
})

export const listPendingResourceGrantsResponseSchema = z.object({
  pending: z.array(pendingResourceGrantSchema),
})

/**
 * The person-first view of one account tree — the account, its sub-accounts, and everything every
 * person holds across them, in one response.
 *
 * WorkOS answers by resource (`listResourceRoleAssignments`), which is the right shape for a
 * check and the wrong shape for the permission picker: "what does Ana hold here?" would be N
 * requests, each repeating the same identity join. This is that regrouping done once, server-side.
 * Rooted at an account because that is the only tree there is — the organization itself is not
 * FGA-addressable, so there is nothing wider to root a person-first query at.
 */
export const accessOverviewQuerySchema = z.object({
  accountId: z.uuid(),
})

export const overviewResourceSchema = z.object({
  resourceType: z.string(),
  resourceId: z.string(),
  name: z.string(),
  /** False until sync-resources (or creation-time registration) has told WorkOS about it. */
  registered: z.boolean(),
})

/** Everything one person holds on one resource of the tree. */
export const personHoldingSchema = z.object({
  resourceType: z.string(),
  resourceId: z.string(),
  capabilities: z.array(heldCapabilitySchema),
  unnamedRoles: z.array(z.object({ roleSlug: z.string(), assignmentIds: z.array(z.string()) })),
})

export const accessOverviewPersonSchema = z.object({
  membershipId: z.string(),
  email: z.string().nullable(),
  name: z.string().nullable(),
  /** One entry per resource they hold anything on — including inherited-only entries. */
  holdings: z.array(personHoldingSchema),
})

/** A pending grant, tagged with the resource it will land on — the overview spans several. */
export const overviewPendingGrantSchema = pendingResourceGrantSchema.extend({
  resourceType: z.string(),
  resourceId: z.string(),
})

/**
 * The people directory — every person who can reach anything, across every account.
 *
 * The org-wide overview answers "who holds what on THIS tree" and costs one WorkOS listing per
 * resource. This answers "who is in the app at all", which is a different question and a much
 * cheaper one: identity comes live from WorkOS in a single memberships call, and the assignments
 * come from the local mirror in a single query. Two round trips instead of one per account and
 * sub-account.
 *
 * `kind` is DERIVED, never stored. A label somebody maintains by hand drifts the first time a
 * person changes jobs, and a directory built on drifting labels is worse than no directory:
 *
 *   - `internal`  — carries an org-wide role that grants something
 *   - `external`  — a membership whose role grants nothing (`client`), so their reach is FGA only
 *   - `pending`   — invited, no membership yet; their access waits in `pending_grants` and they
 *                   appear in no WorkOS membership listing at all
 */
export const directoryKindSchema = z.enum(['internal', 'external', 'pending'])
export type DirectoryKind = z.infer<typeof directoryKindSchema>

/** Where one entry's access comes from — the column an admin needs to know where to revoke. */
export const directorySourceSchema = z.enum(['direct', 'inherited', 'group', 'pending'])
export type DirectorySource = z.infer<typeof directorySourceSchema>

export const directoryEntrySchema = z.object({
  resourceType: z.string(),
  resourceId: z.string(),
  /** The account or sub-account's own name, resolved so the client needs no second lookup. */
  resourceName: z.string(),
  /** The capability's label where a capability names this role, else the bare slug. */
  capability: z.string(),
  roleSlug: z.string(),
  source: directorySourceSchema,
  /** Set only for a time-boxed grant. */
  expiresAt: z.iso.datetime().nullable(),
})

export const directoryPersonSchema = z.object({
  /** Null for a pending invitee — they have no membership to key on yet. */
  membershipId: z.string().nullable(),
  email: z.string().nullable(),
  name: z.string().nullable(),
  kind: directoryKindSchema,
  /** The org-wide role slug. Null for a pending invitee. */
  role: z.string().nullable(),
  entries: z.array(directoryEntrySchema),
})
export type DirectoryPerson = z.infer<typeof directoryPersonSchema>

export const peopleDirectoryResponseSchema = z.object({
  people: z.array(directoryPersonSchema),
  /**
   * When the assignment mirror was last reconciled against WorkOS, or null if never.
   *
   * Shown rather than hidden: the entries come from the mirror, so this is how fresh the answer
   * is. A directory that cannot say how current it is invites being trusted further than it
   * should be.
   */
  reconciledAt: z.iso.datetime().nullable(),
})
export type PeopleDirectoryResponse = z.infer<typeof peopleDirectoryResponseSchema>

/**
 * Someone who can reach this account through the PLATFORM plane — an org-wide role carrying
 * `manage-all:accounts` — rather than any FGA grant on it.
 *
 * These people never show up in `people` above, because their reach is a bypass, not an assignment
 * on the resource. Surfacing them read-only is what makes "who can reach this account" the whole
 * truth instead of only the granted half. Org-wide by nature, so the same set for every account —
 * and not revocable here: their reach comes from a role, removed on the Access page, not this tree.
 */
export const platformReacherSchema = z.object({
  membershipId: z.string(),
  email: z.string().nullable(),
  name: z.string().nullable(),
  roleSlug: z.string(),
  roleName: z.string(),
})
export type PlatformReacher = z.infer<typeof platformReacherSchema>

export const accessOverviewResponseSchema = z.object({
  /** The account first, then its sub-accounts — the row order the picker renders. */
  resources: z.array(overviewResourceSchema),
  people: z.array(accessOverviewPersonSchema),
  pending: z.array(overviewPendingGrantSchema),
  /** Time-boxed access in force anywhere on this tree. */
  grants: z.array(temporaryGrantSchema),
  /** Read-only: staff who reach this account via an org-wide role, not an assignment on it. */
  platformReachers: z.array(platformReacherSchema),
})

/**
 * The organization-wide flavour of the overview — every account tree at once, for the Access
 * control page's person-first picker. Same building blocks, different root: `trees` carries one
 * account-first resource list per account, because the cascade preview and the apply's per-tree
 * split both need to know which sub-accounts belong to which account. People, pending and grants
 * are org-wide unions, matched to trees by resource id.
 */
export const orgAccessOverviewResponseSchema = z.object({
  trees: z.array(z.object({ resources: z.array(overviewResourceSchema) })),
  people: z.array(accessOverviewPersonSchema),
  pending: z.array(overviewPendingGrantSchema),
  grants: z.array(temporaryGrantSchema),
})

/** One resource's worth of capability changes inside an apply. */
export const applyAccessEntrySchema = resourceRefSchema.extend({
  capabilityIds: z.array(z.string().min(1).max(64)).min(1).max(16),
})

/**
 * The picker's save: one person's access across one account tree, applied as a diff.
 *
 * One request rather than one per resource, for the same reason granting takes a capability list:
 * somebody who isn't a member yet gets ONE invitation covering every entry, not an email per
 * resource. `accountId` roots the request — every entry must name the account or one of its
 * sub-accounts, which is what lets a single account-level gate cover the whole apply.
 */
export const applyAccessSchema = z
  .object({
    accountId: z.uuid(),
    email: z.email(),
    /** Capabilities to add, per resource. The baseline belongs in each list; the server doesn't add it. */
    grants: z.array(applyAccessEntrySchema).max(32).default([]),
    /** Capabilities to take away, per resource. Ignored capabilities that don't apply are no-ops. */
    revokes: z.array(applyAccessEntrySchema).max(32).default([]),
    /** Applies to the grants only — a revocation has no deadline to carry. */
    expiresAt: z.iso.datetime().optional(),
    reason: z.string().max(500).optional(),
  })
  .refine((a) => a.grants.length > 0 || a.revokes.length > 0, {
    message: 'nothing to apply — name at least one grant or revocation',
    path: ['grants'],
  })

export const applyAccessResponseSchema = z.object({
  /** 'assigned' when they were already a member; 'invited' when the grants wait on their sign-in. */
  outcome: z.enum(['assigned', 'invited']),
  email: z.string(),
  /** Role assignments actually written or promised — already-held ones don't count. */
  granted: z.number(),
  removed: z.number(),
})

/**
 * What the CALLER may do on one resource. The union of every role they hold on it, including
 * roles inherited from an ancestor resource and from groups — which is why it's a WorkOS query
 * rather than something the client could compute from the session.
 */
export const myResourcePermissionsResponseSchema = z.object({
  resourceType: z.string(),
  resourceId: z.string(),
  permissions: z.array(z.string()),
})

export type ResourceRef = z.infer<typeof resourceRefSchema>
export type AssignResourceRole = z.infer<typeof assignResourceRoleSchema>
export type ResourceAssignment = z.infer<typeof resourceAssignmentSchema>
export type HeldCapability = z.infer<typeof heldCapabilitySchema>
export type ResourceAccessPerson = z.infer<typeof resourceAccessPersonSchema>
export type GrantCapability = z.infer<typeof grantCapabilitySchema>
export type GrantCapabilityResponse = z.infer<typeof grantCapabilityResponseSchema>
export type RevokeCapability = z.infer<typeof revokeCapabilitySchema>
export type ListResourceAssignmentsResponse = z.infer<typeof listResourceAssignmentsResponseSchema>
export type InviteToResource = z.infer<typeof inviteToResourceSchema>
export type InviteToResourceResponse = z.infer<typeof inviteToResourceResponseSchema>
export type PendingResourceGrant = z.infer<typeof pendingResourceGrantSchema>
export type ListPendingResourceGrantsResponse = z.infer<
  typeof listPendingResourceGrantsResponseSchema
>
export type MyResourcePermissionsResponse = z.infer<typeof myResourcePermissionsResponseSchema>
export type OverviewResource = z.infer<typeof overviewResourceSchema>
export type PersonHolding = z.infer<typeof personHoldingSchema>
export type AccessOverviewPerson = z.infer<typeof accessOverviewPersonSchema>
export type OverviewPendingGrant = z.infer<typeof overviewPendingGrantSchema>
export type AccessOverviewResponse = z.infer<typeof accessOverviewResponseSchema>
export type OrgAccessOverviewResponse = z.infer<typeof orgAccessOverviewResponseSchema>
export type ApplyAccessEntry = z.infer<typeof applyAccessEntrySchema>
export type ApplyAccess = z.infer<typeof applyAccessSchema>
export type ApplyAccessResponse = z.infer<typeof applyAccessResponseSchema>
